import React, { useEffect, useState, useCallback } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdChevronLeft, MdChevronRight, MdSearch, MdClose, MdPause, MdPlayArrow, MdDelete, MdOpenInNew } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { userDisplayName } from 'utils/userName';

interface OfferItem {
    _id: string;
    offerType: 'marketplace' | 'direct';
    industry?: string;
    subIndustry?: string;
    status?: string;
    name?: string;
    sellerId?: { _id: string; name?: string; surname?: string; company?: string; email: string } | null;
    buyerId?: { _id: string; name?: string; surname?: string; company?: string; email: string } | null;
    sellerPrice?: number;
    buyerPrice?: number;
    leadsGenerated?: number;
    leadCount?: number;
    isAutoSuspended?: boolean;
    leadApproved?: number;
    leadPending?: number;
    leadRejected?: number;
    weeklyQuantity?: string | number;
    offerNumber?: string;
    offerBuyerNumber?: string;
    channels?: string[];
    createdAt: string;
}

type LeadBucket = 'approved' | 'pending' | 'rejected';

interface OfferLead {
    _id: string;
    orderNumber?: string;
    email?: string;
    sellerId?: { _id: string; name?: string; surname?: string; company?: string; email: string } | null;
    leadPrice?: number;
    buyerPrice?: number;
    approvalStatus?: string;
    paidAt?: string;
    createdAt: string;
}

// Leads opened under an offer row by clicking one of its counters.
interface LeadsPanel {
    bucket: LeadBucket;
    loading: boolean;
    leads: OfferLead[];
    limit?: number;
}

const offerKey = (offer: OfferItem) => `${offer.offerType}-${offer._id}`;

// The back-office leads search matches a lead's Mongo _id, so this lands on that exact lead
// with all the admin actions (approve, reject, resolve conflict...).
const openLead = (leadId: string) => {
    window.open(`/back-office/leads?search=${leadId}`, '_blank', 'noopener');
};

const getApprovalColor = (status?: string) => {
    switch (status) {
        case 'approved':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        case 'rejected':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        case 'conflict':
            return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
        default:
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
};

const STATUS_LABELS: Record<string, string> = {
    on_market: 'On Market',
    running: 'Running',
    done: 'Done',
    waiting_subscription: 'Wait. Subscription',
    waiting_payment: 'Wait. Payment',
    new: 'New',
    'in-progress': 'In Progress',
    closed: 'Closed',
    locked: 'Locked (waterfall)',
};


export default function BackOfficeOffers() {
    const [offers, setOffers] = useState<OfferItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [leadsPanels, setLeadsPanels] = useState<Record<string, LeadsPanel>>({});
    const navigate = useNavigate();

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, typeFilter, debouncedSearch]);

    const fetchOffers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params: any = { page, limit: 50 };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (typeFilter !== 'all') params.type = typeFilter;
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

            const response = await axios.get(`${CONFIG.apiUrl}/admin/offers`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setOffers(response.data.offers);
            setTotal(response.data.total);
            setTotalPages(response.data.pages);
        } catch (error) {
            console.error('Error fetching offers:', error);
        } finally {
            setLoading(false);
        }
    };

    // Clicking a lead counter opens the offer's leads with that approval status under its row;
    // clicking the same counter again closes them.
    const showLeads = async (offer: OfferItem, bucket: LeadBucket) => {
        const key = offerKey(offer);
        if (leadsPanels[key]?.bucket === bucket) {
            setLeadsPanels((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            return;
        }
        setLeadsPanels((prev) => ({ ...prev, [key]: { bucket, loading: true, leads: [] } }));
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/offers/${offer.offerType}/${offer._id}/leads`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { approvalStatus: bucket },
            });
            setLeadsPanels((prev) => prev[key]?.bucket === bucket
                ? { ...prev, [key]: { bucket, loading: false, leads: response.data.leads, limit: response.data.limit } }
                : prev);
        } catch (error) {
            console.error('Error fetching offer leads:', error);
            setLeadsPanels((prev) => prev[key]?.bucket === bucket ? { ...prev, [key]: { bucket, loading: false, leads: [] } } : prev);
        }
    };

    const renderLeadsPanel = (panel: LeadsPanel) => {
        if (panel.loading) return <div className="py-2 text-center text-sm text-gray-500">Loading...</div>;
        if (panel.leads.length === 0) return <div className="py-2 text-center text-sm text-gray-500">No leads</div>;
        return (
            <div className="overflow-x-auto">
                {panel.limit && panel.leads.length >= panel.limit && (
                    <div className="mb-2 text-xs text-gray-500">Les {panel.limit} leads les plus récents</div>
                )}
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-navy-600">
                            <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Lead</th>
                            <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Email</th>
                            <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Seller</th>
                            <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Prices</th>
                            <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Approval</th>
                            <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Paid</th>
                            <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Date</th>
                            <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {panel.leads.map((lead) => (
                            <tr
                                key={lead._id}
                                onClick={() => openLead(lead._id)}
                                title="Ouvrir le lead dans un nouvel onglet"
                                className="cursor-pointer border-b border-gray-100 hover:bg-white dark:border-navy-800 dark:hover:bg-navy-800"
                            >
                                <td className="py-2 pr-4 font-mono text-xs text-navy-700 dark:text-white">
                                    {lead.orderNumber || lead._id.slice(-8)}
                                </td>
                                <td className="py-2 pr-4 text-xs">{lead.email || 'N/A'}</td>
                                <td className="py-2 pr-4 text-xs">
                                    {lead.sellerId ? (
                                        <>
                                            <div className="font-medium">{userDisplayName(lead.sellerId)}</div>
                                            <div className="text-[10px] text-gray-500">{lead.sellerId.email}</div>
                                        </>
                                    ) : (
                                        <span className="text-gray-500">Unknown</span>
                                    )}
                                </td>
                                <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-400">
                                    <div><span className="uppercase tracking-wide text-gray-400">S</span> {(lead.leadPrice || 0).toFixed(2)}€</div>
                                    <div><span className="uppercase tracking-wide text-gray-400">B</span> {(lead.buyerPrice || 0).toFixed(2)}€</div>
                                </td>
                                <td className="py-2 pr-4 text-xs">
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${getApprovalColor(lead.approvalStatus)}`}>
                                        {lead.approvalStatus || 'pending'}
                                    </span>
                                </td>
                                <td className="py-2 pr-4 text-xs">
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        lead.paidAt ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                    }`}>
                                        {lead.paidAt ? '✓ Paid' : 'Unpaid'}
                                    </span>
                                </td>
                                <td className="py-2 pr-4 text-xs text-gray-500">
                                    {new Date(lead.createdAt).toLocaleDateString('fr-FR')}
                                </td>
                                <td className="py-2 text-gray-400">
                                    <MdOpenInNew size={16} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'on_market':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'running':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'done':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
            case 'locked':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
            case 'waiting_subscription':
            case 'waiting_payment':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            default:
                return 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-400';
        }
    };

    const handleStatusFilter = useCallback((value: string) => {
        setStatusFilter(value);
        setPage(1);
    }, []);

    const handleTypeFilter = useCallback((value: string) => {
        setTypeFilter(value);
        setPage(1);
    }, []);

    const handlePause = async (e: React.MouseEvent, offer: OfferItem) => {
        e.stopPropagation();
        const token = localStorage.getItem('token');
        await axios.patch(
            `${CONFIG.apiUrl}/admin/offers/${offer.offerType}/${offer._id}`,
            { status: 'done' },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        setOffers((prev) => prev.map((o) => o._id === offer._id ? { ...o, status: 'done' } : o));
    };

    const handleResume = async (e: React.MouseEvent, offer: OfferItem) => {
        e.stopPropagation();
        const token = localStorage.getItem('token');
        await axios.patch(
            `${CONFIG.apiUrl}/admin/offers/${offer.offerType}/${offer._id}`,
            { status: 'running' },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        setOffers((prev) => prev.map((o) => o._id === offer._id ? { ...o, status: 'running' } : o));
    };

    const handleLiftCapAtteint = async (e: React.MouseEvent, offer: OfferItem) => {
        e.stopPropagation();
        if (!window.confirm('Relancer cette offre ?')) return;
        const token = localStorage.getItem('token');
        await axios.patch(
            `${CONFIG.apiUrl}/admin/offers/${offer.offerType}/${offer._id}`,
            { isAutoSuspended: false },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        setOffers((prev) => prev.map((o) => o._id === offer._id ? { ...o, isAutoSuspended: false } : o));
    };

    const handleDelete = async (e: React.MouseEvent, offer: OfferItem) => {
        e.stopPropagation();
        if (!window.confirm(`Delete offer ${offer.offerNumber || offer._id.slice(-8)}? This cannot be undone.`)) return;
        const token = localStorage.getItem('token');
        await axios.delete(
            `${CONFIG.apiUrl}/admin/offers/${offer.offerType}/${offer._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
        );
        setOffers((prev) => prev.filter((o) => o._id !== offer._id));
        setTotal((prev) => prev - 1);
    };

    // Pagination page numbers
    const getPageNumbers = () => {
        const pages: (number | '...')[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push('...');
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                pages.push(i);
            }
            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const startItem = total === 0 ? 0 : (page - 1) * 50 + 1;
    const endItem = Math.min(page * 50, total);

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                {/* Header */}
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Offers Management
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {total.toLocaleString()} total offers
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-5 flex flex-col gap-3">
                    {/* Search + Type */}
                    <div className="flex gap-3">
                        <div className="relative flex-1">
                            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by offer #, industry, name, seller or buyer (name, company, email)..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-gray-500"
                            />
                            {searchInput && (
                                <button
                                    onClick={() => setSearchInput('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <MdClose size={16} />
                                </button>
                            )}
                        </div>
                        <select
                            value={typeFilter}
                            onChange={(e) => handleTypeFilter(e.target.value)}
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        >
                            <option value="all">All Types</option>
                            <option value="marketplace">Marketplace</option>
                            <option value="direct">Direct</option>
                        </select>
                    </div>

                    {/* Status pills */}
                    <div className="flex flex-wrap gap-2">
                        {([
                            { value: 'all', label: 'All' },
                            { value: 'on_market', label: 'On Market' },
                            { value: 'waiting_subscription', label: 'Wait. Subscription' },
                            { value: 'waiting_payment', label: 'Wait. Payment' },
                            { value: 'running', label: 'Running' },
                            { value: 'locked', label: 'Locked' },
                            { value: 'done', label: 'Done' },
                        ] as const).map((s) => (
                            <button
                                key={s.value}
                                onClick={() => handleStatusFilter(s.value)}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                    statusFilter === s.value
                                        ? 'bg-navy-700 text-white dark:bg-brand-400'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Offer</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Industry</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Seller</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Buyer</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Prices</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Leads</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Status</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Date</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="py-8 text-center text-sm text-gray-500">Loading...</td>
                                </tr>
                            ) : offers.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="py-8 text-center text-sm text-gray-500">No offers found</td>
                                </tr>
                            ) : (
                                offers.map((offer) => {
                                    const margin = (offer.buyerPrice != null && offer.sellerPrice != null)
                                        ? offer.buyerPrice - offer.sellerPrice
                                        : null;

                                    const panel = leadsPanels[offerKey(offer)];
                                    const counter = (bucket: LeadBucket, label: string, count: number, className: string, suffix = '') => (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); showLeads(offer, bucket); }}
                                            disabled={count === 0}
                                            title="Voir les leads"
                                            className={`text-left hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-60 ${className} ${
                                                panel?.bucket === bucket ? 'underline' : ''
                                            }`}
                                        >
                                            {label}: {count}{suffix}
                                        </button>
                                    );

                                    return (
                                        <React.Fragment key={offerKey(offer)}>
                                        <tr
                                            className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                                            onClick={() => navigate(`/back-office/offers/${offer.offerType}/${offer._id}`)}
                                        >
                                            {/* Offer # + type */}

                                            <td className="py-3 pr-4">
                                                {offer.offerNumber && (
                                                    <div className="font-mono text-xs text-navy-700 dark:text-white">
                                                        <span className="text-gray-400">S:</span> {offer.offerNumber}
                                                    </div>
                                                )}
                                                {offer.offerBuyerNumber && (
                                                    <div className="font-mono text-xs text-navy-700 dark:text-white">
                                                        <span className="text-gray-400">B:</span> {offer.offerBuyerNumber}
                                                    </div>
                                                )}
                                                {!offer.offerNumber && !offer.offerBuyerNumber && (
                                                    <div className="font-mono text-xs text-gray-400">{offer._id.slice(-8)}</div>
                                                )}
                                                {offer.name && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]" title={offer.name}>
                                                        {offer.name}
                                                    </div>
                                                )}
                                                <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                    offer.offerType === 'marketplace'
                                                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                                        : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                                                }`}>
                                                    {offer.offerType === 'marketplace' ? 'Marketplace' : 'Direct'}
                                                </span>
                                            </td>

                                            {/* Industry */}
                                            <td className="py-3 pr-4 text-sm">
                                                <div className="font-medium text-navy-700 dark:text-white">{offer.industry || '-'}</div>
                                                {offer.subIndustry && (
                                                    <div className="text-xs text-gray-500">{offer.subIndustry}</div>
                                                )}
                                            </td>

                                            {/* Seller */}
                                            <td className="py-3 pr-4">
                                                {offer.sellerId ? (
                                                    <div>
                                                        <div className="text-sm font-medium text-navy-700 dark:text-white">
                                                            {userDisplayName(offer.sellerId)}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{offer.sellerId.email}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">— No seller</span>
                                                )}
                                            </td>

                                            {/* Buyer */}
                                            <td className="py-3 pr-4">
                                                {offer.buyerId ? (
                                                    <div>
                                                        <div className="text-sm font-medium text-navy-700 dark:text-white">
                                                            {userDisplayName(offer.buyerId)}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{offer.buyerId.email}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">— No buyer</span>
                                                )}
                                            </td>

                                            {/* Prices + margin */}
                                            <td className="py-3 pr-4 text-sm">
                                                <div className="text-gray-600 dark:text-gray-400">
                                                    <span className="text-xs uppercase tracking-wide text-gray-400">S</span>{' '}
                                                    {offer.sellerPrice != null ? `${offer.sellerPrice.toFixed(2)}€` : '-'}
                                                </div>
                                                <div className="text-gray-600 dark:text-gray-400">
                                                    <span className="text-xs uppercase tracking-wide text-gray-400">B</span>{' '}
                                                    {offer.buyerPrice != null ? `${offer.buyerPrice.toFixed(2)}€` : '-'}
                                                </div>
                                                {margin != null && (
                                                    <div className="text-xs font-semibold text-green-600 dark:text-green-400">
                                                        +{margin.toFixed(2)}€ margin
                                                    </div>
                                                )}
                                            </td>

                                            {/* Leads breakdown */}
                                            <td className="py-3 pr-4">
                                                {(() => {
                                                    const approved = offer.leadApproved ?? 0;
                                                    const pending = offer.leadPending ?? 0;
                                                    const rejected = offer.leadRejected ?? 0;
                                                    const weekly = Number(offer.weeklyQuantity) || 0;
                                                    const remaining = weekly > 0 ? Math.max(0, weekly - approved) : null;
                                                    return (
                                                        <div className="flex flex-col items-start gap-0.5 text-xs">
                                                            {counter('approved', 'Approved', approved, 'font-semibold text-green-600 dark:text-green-400', weekly > 0 ? ` / ${weekly}` : '')}
                                                            {counter('pending', 'Pending', pending, 'text-yellow-600 dark:text-yellow-400', remaining != null ? ` / ${remaining}` : '')}
                                                            {counter('rejected', 'Rejected', rejected, 'text-red-500 dark:text-red-400')}
                                                            {offer.leadsGenerated != null && (
                                                                <span className="mt-0.5 text-gray-400">
                                                                    Count: {offer.leadsGenerated}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>

                                            {/* Status */}
                                            <td className="py-3 pr-4">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(offer.status)}`}>
                                                        {STATUS_LABELS[offer.status || ''] || offer.status || 'N/A'}
                                                    </span>
                                                    {offer.isAutoSuspended && (
                                                        <span
                                                            className="inline-flex rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                                        >
                                                            ⏸ cap atteint
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Date */}
                                            <td className="py-3 pr-4 text-sm text-gray-600 dark:text-gray-400">
                                                {new Date(offer.createdAt).toLocaleDateString('fr-FR')}
                                            </td>

                                            {/* Actions */}
                                            <td className="py-3" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-1">
                                                    {offer.isAutoSuspended && (
                                                        <button
                                                            title="Lever la suspension (cap atteint)"
                                                            onClick={(e) => handleLiftCapAtteint(e, offer)}
                                                            className="rounded p-1 text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-navy-600"
                                                        >
                                                            <MdPlayArrow size={18} />
                                                        </button>
                                                    )}
                                                    {offer.status === 'running' && (
                                                        <button
                                                            title="Mark as done"
                                                            onClick={(e) => handlePause(e, offer)}
                                                            className="rounded p-1 text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-navy-600"
                                                        >
                                                            <MdPause size={18} />
                                                        </button>
                                                    )}
                                                    {(offer.status === 'done' || offer.status === 'closed') && (
                                                        <button
                                                            title="Resume"
                                                            onClick={(e) => handleResume(e, offer)}
                                                            className="rounded p-1 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-navy-600"
                                                        >
                                                            <MdPlayArrow size={18} />
                                                        </button>
                                                    )}
                                                    <button
                                                        title="Delete"
                                                        onClick={(e) => handleDelete(e, offer)}
                                                        className="rounded p-1 text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-navy-600"
                                                    >
                                                        <MdDelete size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {panel && (
                                            <tr className="border-b border-gray-100 bg-gray-50 dark:border-navy-700 dark:bg-navy-900">
                                                <td colSpan={9} className="px-4 py-3">
                                                    {renderLeadsPanel(panel)}
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {total > 0 ? `Showing ${startItem}–${endItem} of ${total.toLocaleString()} offers` : 'No offers'}
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className="flex items-center rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                                >
                                    <MdChevronLeft size={18} />
                                </button>
                                {getPageNumbers().map((p, i) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${i}`} className="px-2 text-sm text-gray-400">...</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => setPage(p)}
                                            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                                                page === p
                                                    ? 'bg-navy-700 text-white dark:bg-brand-400'
                                                    : 'border border-gray-300 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700 dark:text-white'
                                            }`}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages}
                                    className="flex items-center rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                                >
                                    <MdChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
